import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import FeedbackSettingsModal from '../components/FeedbackSettingsModal';
import TrainingCertificates from '../components/TrainingCertificates';
import { FEEDBACK_STATUS, TEMPLATE_NAMES } from '../utils/feedback';
import {
  STATUS_BADGES, formatWindow, formatDay, toDateInput, toTimeInput, nextDate
} from '../utils/attendance';

const TrainingDetail = () => {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [error, setError] = useState('');
  const [dayForm, setDayForm] = useState(null);
  const [renameForm, setRenameForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedbackForms, setFeedbackForms] = useState([]);
  const [creatingFeedback, setCreatingFeedback] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/attendance/trainings/${tid}`);
      setTraining(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load training');
    }
    api.get('/feedback', { params: { training_id: tid } }).then(r => setFeedbackForms(r.data)).catch(() => {});
  }, [tid]);

  useEffect(() => { load(); }, [load]);

  if (!training) {
    return error
      ? <div className="alert alert-error">{error}</div>
      : <div className="text-center mt-5">Loading...</div>;
  }

  const { days } = training;

  // Next day: numbered on from the last, the next working date, same scan times.
  const openAddDay = () => {
    const last = days[days.length - 1];
    setDayForm(last
      ? {
          title: `Day ${days.length + 1}`,
          date: nextDate(toDateInput(last.starts_at), true),
          startTime: toTimeInput(last.starts_at),
          endTime: toTimeInput(last.ends_at)
        }
      : { title: 'Day 1', date: toDateInput(new Date()), startTime: '09:45', endTime: '10:30' });
  };

  const addDay = async (e) => {
    e.preventDefault();
    if (dayForm.endTime <= dayForm.startTime) {
      alert('The scan window must end after it starts');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/attendance/trainings/${tid}/days`, {
        title: dayForm.title,
        starts_at: new Date(`${dayForm.date}T${dayForm.startTime}`).toISOString(),
        ends_at: new Date(`${dayForm.date}T${dayForm.endTime}`).toISOString()
      });
      setDayForm(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add day');
    } finally {
      setSaving(false);
    }
  };

  const rename = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/attendance/trainings/${tid}`, { title: renameForm });
      setRenameForm(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename');
    }
  };

  const remove = async () => {
    const records = days.reduce((n, d) => n + d.present_count, 0);
    if (!window.confirm(
      `Delete "${training.title}" with its ${days.length} day(s) and ${records} attendance record(s)? This cannot be undone.`
    )) return;
    try {
      await api.delete(`/attendance/trainings/${tid}`);
      navigate('/attendance');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const download = async () => {
    try {
      const response = await api.get(`/attendance/trainings/${tid}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Training_Attendance_${training.title.replace(/[^a-zA-Z0-9]+/g, '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download attendance');
    }
  };

  return (
    <div>
      <Link to="/attendance" className="text-muted text-sm">← All attendance</Link>
      <div className="flex flex-between flex-center mb-4 mt-2">
        <div>
          <h1 style={{ marginBottom: '4px' }}>{training.title}</h1>
          <p className="text-muted">
            {days.length > 0
              ? `${formatDay(days[0].starts_at)} – ${formatDay(days[days.length - 1].ends_at)} · ${days.length} day${days.length === 1 ? '' : 's'}`
              : 'No days yet'}
            {` · ${training.trainee_count} trainee${training.trainee_count === 1 ? '' : 's'}`}
            {training.category_name && ` · ${training.category_name}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-success btn-sm" onClick={download} disabled={days.length === 0}>
            Download Excel (all days)
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setRenameForm(training.title)}>Rename</button>
          <button className="btn btn-danger btn-sm" onClick={remove}>Delete</button>
        </div>
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        <div className="flex flex-between flex-center" style={{ padding: '16px 20px' }}>
          <h3 style={{ margin: 0 }}>Days</h3>
          <button className="btn btn-primary btn-sm" onClick={openAddDay}>+ Add day</button>
        </div>
        {days.length === 0 ? (
          <p className="text-muted text-center" style={{ padding: '0 20px 20px' }}>No days yet. Add the first one.</p>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Day</th><th>Scan window</th><th>Status</th><th>Present</th><th></th></tr>
            </thead>
            <tbody>
              {days.map(d => (
                <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/attendance/${d.id}`)}>
                  <td><strong>{d.title}</strong></td>
                  <td className="text-sm">{formatWindow(d.starts_at, d.ends_at)}</td>
                  <td><span className={`badge ${STATUS_BADGES[d.status].className}`}>{STATUS_BADGES[d.status].label}</span></td>
                  <td>{d.present_count}</td>
                  <td className="text-muted" style={{ textAlign: 'right' }}>QR code ›</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-muted text-sm mt-3">
        Open a day to show its QR code. "Download Excel (all days)" lists every trainee with a ✓ or ✗ per day
        and whether they attended every day.
      </p>

      {/* Feedback forms for this training */}
      <div className="card mt-4" style={{ padding: 0 }}>
        <div className="flex flex-between flex-center" style={{ padding: '16px 20px' }}>
          <h3 style={{ margin: 0 }}>Feedback</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setCreatingFeedback(true)}>+ Feedback form</button>
        </div>
        {feedbackForms.length === 0 ? (
          <p className="text-muted text-center" style={{ padding: '0 20px 20px' }}>
            No feedback form yet. Create one to show its QR code at the end of the course.
          </p>
        ) : (
          <table className="table">
            <thead><tr><th>Form</th><th>Questions</th><th>Status</th><th>Responses</th><th></th></tr></thead>
            <tbody>
              {feedbackForms.map(f => (
                <tr key={f.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/feedback/${f.id}`)}>
                  <td><strong>{f.title}</strong></td>
                  <td className="text-sm">{TEMPLATE_NAMES[f.template]}</td>
                  <td><span className={`badge ${FEEDBACK_STATUS[f.status].className}`}>{FEEDBACK_STATUS[f.status].label}</span></td>
                  <td>{f.response_count}</td>
                  <td className="text-muted" style={{ textAlign: 'right' }}>QR code ›</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TrainingCertificates training={training} />

      {creatingFeedback && (
        <FeedbackSettingsModal
          trainingId={training.id}
          onClose={() => setCreatingFeedback(false)}
          onSaved={(f) => navigate(`/feedback/${f.id}`)}
        />
      )}

      {dayForm && (
        <div className="modal-overlay" {...overlayDismiss(() => setDayForm(null))}>
          <div className="modal">
            <div className="modal-header">
              <h3>Add Day</h3>
              <button className="modal-close" onClick={() => setDayForm(null)}>&times;</button>
            </div>
            <form onSubmit={addDay}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" required maxLength={255} value={dayForm.title}
                    onChange={e => setDayForm({ ...dayForm, title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" required value={dayForm.date}
                    onChange={e => setDayForm({ ...dayForm, date: e.target.value })} />
                </div>
                <div className="grid grid-2">
                  <div className="form-group">
                    <label>Scan opens</label>
                    <input type="time" required value={dayForm.startTime}
                      onChange={e => setDayForm({ ...dayForm, startTime: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Scan closes</label>
                    <input type="time" required value={dayForm.endTime}
                      onChange={e => setDayForm({ ...dayForm, endTime: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDayForm(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {renameForm !== null && (
        <div className="modal-overlay" {...overlayDismiss(() => setRenameForm(null))}>
          <div className="modal">
            <div className="modal-header">
              <h3>Rename Training</h3>
              <button className="modal-close" onClick={() => setRenameForm(null)}>&times;</button>
            </div>
            <form onSubmit={rename}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Training name</label>
                  <input type="text" required maxLength={255} autoFocus value={renameForm}
                    onChange={e => setRenameForm(e.target.value)} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setRenameForm(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDetail;
